/**
 * MainMenu Component
 * Horizontal navigation menu with submenu support
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem as MuiMenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetIdentity } from '@refinedev/core';
import { navigationConfig } from '../../config/navigation';
import { MenuItem } from '../../types/navigation';
import { usePermission } from '../../hooks/usePermission';

export interface MainMenuProps {
  /** Optional additional styling */
  sx?: object;
}

/**
 * MainMenu - Horizontal navigation with dropdown submenu
 */
export const MainMenu: React.FC<MainMenuProps> = ({ sx }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: identity } = useGetIdentity<{
    id: string;
    name: string;
    email: string;
    fullName?: string;
  }>();

  // Navigation state
  const [submenuAnchor, setSubmenuAnchor] = useState<null | HTMLElement>(null);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

  // 전체 메뉴 너비 측정을 위한 ref와 state
  const menuContainerRef = useRef<HTMLDivElement>(null);
  const [fullMenuWidth, setFullMenuWidth] = useState<number>(0);

  // Hover timeout for smooth UX
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 기능 권한 체크 (permissionKey 기반)
  const { hasPermission: hasFunctionPermission } = usePermission();

  // permissionKey 기반으로 메뉴 필터링
  const filteredNavConfig = navigationConfig
    .filter(item => !item.permissionKey || hasFunctionPermission(item.permissionKey))
    .map(item => {
      if (!item.submenu) return item;
      return {
        ...item,
        submenu: item.submenu.filter(
          sub => !sub.permissionKey || hasFunctionPermission(sub.permissionKey)
        ),
      };
    });

  // 사용자 역할 권한 확인 함수 (JWT 기반, 기존 호환)
  const hasPermission = (requiredPermission?: string): boolean => {
    if (!requiredPermission) return true; // 권한 요구사항이 없으면 접근 허용

    // JWT 토큰에서 역할 정보 추출 (localStorage와 sessionStorage 모두 확인)
    const token =
      localStorage.getItem('dream_main_token') || sessionStorage.getItem('dream_main_token');
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const authField = payload.auth; // "ROLE_ADMIN" 또는 "ROLE_ADMIN,메뉴권한1,메뉴권한2" 등

      if (!authField) return false;

      // auth 필드는 쉼표로 구분된 권한 목록일 수 있음
      const authorities = typeof authField === 'string' ? authField.split(',') : [];

      // "ROLE_ADMIN"인 경우만 admin 권한 부여
      if (requiredPermission === 'admin') {
        return authorities.includes('ROLE_ADMIN');
      }

      return true;
    } catch (error) {
      console.error('권한 확인 실패:', error);
      return false;
    }
  };

  // 전체 메뉴(navigationConfig) 기준 너비 측정 - 가운데 정렬 시 좌측 여백 계산용
  useEffect(() => {
    const measureFullMenuWidth = () => {
      // navigationConfig 전체 메뉴 아이템 수 기준으로 너비 계산
      // 각 버튼: minWidth 100px + px:2 (좌우 16px씩) = 최소 132px
      // 실제 텍스트 길이에 따라 달라지므로 DOM 측정 사용
      if (menuContainerRef.current) {
        const buttons = menuContainerRef.current.querySelectorAll(':scope > div');
        let totalWidth = 0;
        buttons.forEach(btn => {
          totalWidth += btn.getBoundingClientRect().width;
        });
        // 전체 메뉴 아이템 수와 필터된 메뉴 아이템 수가 다르면
        // navigationConfig 기준 비율로 전체 너비 추정
        if (filteredNavConfig.length > 0 && filteredNavConfig.length < navigationConfig.length) {
          // 필터된 메뉴의 평균 너비로 전체 메뉴 너비 추정
          const avgWidth = totalWidth / filteredNavConfig.length;
          setFullMenuWidth(avgWidth * navigationConfig.length);
        } else {
          setFullMenuWidth(totalWidth);
        }
      }
    };

    measureFullMenuWidth();
    window.addEventListener('resize', measureFullMenuWidth);
    return () => window.removeEventListener('resize', measureFullMenuWidth);
  }, [filteredNavConfig.length]);

  // Close submenu when pathname changes (페이지 이동 시 서브메뉴 닫기)
  useEffect(() => {
    // Clear any pending hover timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    // Close submenu
    setSubmenuAnchor(null);
    setOpenSubmenu(null);
  }, [location.pathname]);

  // Check if menu item is active
  const isActiveMenu = (item: MenuItem): boolean => {
    // Exact match
    if (location.pathname === item.path) {
      return true;
    }

    // Check if any submenu item is active
    if (item.submenu) {
      return item.submenu.some(subItem => location.pathname === subItem.path);
    }

    // Partial match for nested routes
    if (location.pathname.startsWith(item.path) && item.path !== '/') {
      return true;
    }

    return false;
  };

  // Handle menu hover
  const handleMenuHover = (event: React.MouseEvent<HTMLElement>, item: MenuItem) => {
    if (!item.submenu) return;

    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    const target = event.currentTarget; // DOM 요소를 동기적으로 캡처 (setTimeout 내에서 event.currentTarget은 null)
    // Open submenu after short delay (only if not already handling this menu)
    hoverTimeoutRef.current = setTimeout(() => {
      // Only open if this menu isn't already open
      if (openSubmenu !== item.path) {
        setSubmenuAnchor(target);
        setOpenSubmenu(item.path);
      }
    }, 150);
  };

  // Handle menu leave
  const handleMenuLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  // Handle menu click (Box 요소에서 처리하여 hover와 동일한 anchor 사용)
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, item: MenuItem) => {
    // Clear any pending hover timeout to prevent race condition
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    if (item.submenu) {
      // Always open on first click (don't toggle if already open)
      setSubmenuAnchor(event.currentTarget);
      setOpenSubmenu(item.path);
    } else {
      // Navigate to page
      navigate(item.path);
    }
  };

  // Handle submenu close
  const handleSubmenuClose = () => {
    setSubmenuAnchor(null);
    setOpenSubmenu(null);
  };

  // Handle submenu item click
  const handleSubmenuClick = (subItem: MenuItem) => {
    // Close submenu immediately
    handleSubmenuClose();

    // Navigate after a brief delay to allow menu to close
    setTimeout(() => {
      navigate(subItem.path);
    }, 100);
  };

  return (
    <>
      <Box
        ref={menuContainerRef}
        sx={{
          display: 'flex',
          gap: 0,
          height: '50px',
          justifyContent: 'flex-start',
          width: '100%',
          maxWidth: fullMenuWidth > 0 ? `${fullMenuWidth}px` : 'none',
          mx: 'auto',
          ...sx,
        }}
      >
        {filteredNavConfig.map(item => {
          const isActive = isActiveMenu(item);
          const hasSubmenu = Boolean(item.submenu);

          return (
            <Box
              key={item.path}
              onMouseEnter={e => handleMenuHover(e, item)}
              onMouseLeave={handleMenuLeave}
              onClick={e => handleMenuClick(e, item)}
              sx={{ cursor: 'pointer' }}
            >
              <Button
                sx={{
                  color: isActive ? '#000000' : '#333333',
                  minWidth: '100px',
                  height: isActive ? '50px' : '42px',
                  px: 2,
                  py: isActive ? 1.5 : 1,
                  display: 'flex',
                  alignItems: 'flex-end',
                  borderRadius: 0,
                  borderBottom: isActive ? '2px solid #0e0e0e' : 'none',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    color: '#000000',
                  },
                  fontWeight: 400,
                  fontSize: '16px',
                  letterSpacing: '-0.5px',
                  lineHeight: '10px',
                  transition: 'all 200ms ease-out',
                  textTransform: 'none',
                  pointerEvents: 'none',
                }}
              >
                {item.label}
              </Button>
            </Box>
          );
        })}
      </Box>

      {/* Render only the active submenu */}
      {filteredNavConfig.map(item => {
        if (!item.submenu || openSubmenu !== item.path) return null;

        return (
          <Menu
            key={item.path}
            anchorEl={submenuAnchor}
            open={submenuAnchor !== null}
            onClose={handleSubmenuClose}
            MenuListProps={{
              onMouseLeave: handleSubmenuClose,
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                minWidth: 240,
                mt: 0,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                borderRadius: 1,
              },
            }}
            TransitionProps={{
              timeout: 200,
            }}
          >
            {item.submenu
              ?.filter(subItem => hasPermission(subItem.requiredPermission))
              .map(subItem => {
                const isSubmenuActive = location.pathname === subItem.path;

                return (
                  <MuiMenuItem
                    key={subItem.path}
                    onClick={() => handleSubmenuClick(subItem)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      minHeight: 44,
                      backgroundColor: isSubmenuActive ? 'rgba(0, 63, 95, 0.08)' : 'transparent',
                      borderLeft: isSubmenuActive
                        ? `3px solid ${theme.palette.primary.main}`
                        : '3px solid transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 63, 95, 0.04)',
                      },
                    }}
                  >
                    {subItem.icon && (
                      <ListItemIcon
                        sx={{
                          minWidth: 32,
                          color: isSubmenuActive ? theme.palette.primary.main : 'text.secondary',
                        }}
                      >
                        {React.cloneElement(subItem.icon, { fontSize: 'small' })}
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={subItem.label}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: isSubmenuActive ? 600 : 400,
                      }}
                    />
                  </MuiMenuItem>
                );
              })}
          </Menu>
        );
      })}
    </>
  );
};
